import type { AnalysisState, QueryResult } from '@aibi/contracts';
import { Button, EmptyState, Spinner } from '@aibi/ui';
import { checkCompatibility } from '@aibi/visualization-engine';
import { describeQueryShape } from '@aibi/analysis-engine';
import { CircleAlert, MousePointerClick, TriangleAlert } from 'lucide-react';
import { Suspense, useCallback, useMemo } from 'react';
import { useServices } from '../../../app/services';
import type { ValueEvent } from '../../../visualization/types';
import { visualizationSurfaceStyle } from '../../../visualization/viz-style';
import { PREVIEW_WARNING_CODE } from '../../services/preview-query-result-source';
import { useCardPlan, type CardPlan } from '../../state/use-card-plan';
import { useQueryResult } from '../../state/use-query-result';
import { useUiState } from '../../state/ui-state';
import { useWorkspace, useWorkspaceActions } from '../../state/workspace-context';

export interface CardBodyProps {
  readonly card: AnalysisState;
  readonly title: string;
}

const fallback = (
  <div className="flex h-full items-center justify-center">
    <Spinner label="Cargando visualizacion" />
  </div>
);

/**
 * Cuerpo de una tarjeta: planifica con el Analysis Engine, obtiene el resultado por el
 * puerto de datos y delega en el renderer registrado. Gestiona los estados vacio, no
 * configurado, incompatible, cargando y error.
 */
export function CardBody({ card, title }: CardBodyProps) {
  const plan = useCardPlan(card);
  return <CardBodyContent card={card} title={title} plan={plan} />;
}

function CardBodyContent({ card, title, plan }: CardBodyProps & { readonly plan: CardPlan }) {
  const { index } = useWorkspace();
  const { execute } = useWorkspaceActions();
  const { rendererRegistry, visualizationCatalog } = useServices();
  const { colorScheme } = useUiState();
  const query = useQueryResult(plan.status === 'planned' ? plan.plan : null, card);

  const compatibility = useMemo(() => {
    const descriptor = visualizationCatalog.get(card.visualization.type);
    return descriptor ? checkCompatibility(descriptor, describeQueryShape(card, index)) : null;
  }, [card, index, visualizationCatalog]);

  const onSelect = useCallback(
    (event: ValueEvent) => {
      execute({
        type: 'toggle-selection',
        cardId: card.id,
        dimensionId: event.fieldId,
        value: event.value,
        additive: event.additive,
      });
    },
    [card.id, execute],
  );
  const onDrill = useCallback(
    (event: ValueEvent) => {
      execute({ type: 'drill-down', cardId: card.id, key: event.columnKey, value: event.value });
    },
    [card.id, execute],
  );

  if (plan.status === 'empty') {
    return (
      <EmptyState
        icon={<MousePointerClick />}
        title="Analisis sin configurar"
        description="Arrastre dimensiones y medidas desde el explorador, o use el boton + de cada campo."
      />
    );
  }
  if (plan.status === 'invalid') {
    const messages = plan.error.diagnostics
      .filter((diagnostic) => diagnostic.severity === 'error')
      .map((diagnostic) => diagnostic.message);
    return (
      <EmptyState
        tone="error"
        icon={<CircleAlert />}
        title="No se puede planificar este analisis"
        description={
          <ul className="space-y-0.5">
            {messages.slice(0, 3).map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        }
      />
    );
  }
  if (compatibility && !compatibility.compatible) {
    return (
      <EmptyState
        icon={<TriangleAlert />}
        title="Visualizacion incompatible con los campos actuales"
        description={compatibility.reasons.join(' ')}
        action={
          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              execute({ type: 'set-visualization-type', cardId: card.id, visualization: 'table' });
            }}
          >
            Ver como tabla
          </Button>
        }
      />
    );
  }
  if (query.status === 'idle' || query.status === 'loading') return fallback;
  if (query.status === 'error') {
    return (
      <EmptyState
        tone="error"
        icon={<CircleAlert />}
        title="No se pudieron obtener los datos"
        description={query.error.message}
      />
    );
  }

  const definition = rendererRegistry.resolve(card.visualization.type);
  const Renderer = definition.component;
  const notice = resultNotice(query.result);
  const surfaceStyle = visualizationSurfaceStyle(card.visualization);
  const renderer = (
    <div className="h-full min-h-0" style={surfaceStyle}>
      <Suspense fallback={fallback}>
        <Renderer
          spec={card.visualization}
          result={query.result}
          selections={card.selections}
          density={card.layout.density}
          colorScheme={colorScheme}
          label={title}
          {...(card.visualization.interaction.selectable ? { onSelect } : {})}
          {...(card.interaction.drillMode === 'hierarchy' ? { onDrill } : {})}
        />
      </Suspense>
    </div>
  );
  if (notice === null) return renderer;
  return (
    <div className="flex h-full flex-col">
      <p
        role="note"
        title={notice.detail}
        className="flex shrink-0 items-center gap-1 truncate px-1 pb-1 text-[11px] text-warning"
      >
        <TriangleAlert aria-hidden className="size-3 shrink-0" />
        {notice.text}
      </p>
      <div className="min-h-0 flex-1">{renderer}</div>
    </div>
  );
}

/** Aviso no bloqueante para resultados reales truncados o con advertencias del motor. */
function resultNotice(result: QueryResult): { text: string; detail: string } | null {
  const warnings = result.warnings.filter((warning) => warning.code !== PREVIEW_WARNING_CODE);
  if (result.execution.truncated || result.pagination.hasMore) {
    return {
      text: `Se muestran las primeras ${String(result.rows.length)} filas.`,
      detail: warnings.map((warning) => warning.message).join(' '),
    };
  }
  const [first] = warnings;
  if (!first) return null;
  return { text: first.message, detail: warnings.map((warning) => warning.message).join(' ') };
}
